import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { getAllOrders, getBranchOrders } from '../services/orderService';
import { useAuth } from './AuthContext';
import { useBranch } from './BranchContext';

const OrderContext = createContext();

export const ORDER_STATUS_CATEGORIES = {
  CONFIRMED: 'Confirmed',
  PREPARING: 'Preparing',
  READY_TO_PICKUP: 'Ready to Pickup'
};

const initialState = {
  orders: [],
  loading: false,
  error: null,
  selectedBranch: null,
  orderVersions: {},
  filters: {
    status: null,
    dateRange: null
  }
};

const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ORDERS: 'SET_ORDERS',
  SET_ERROR: 'SET_ERROR',
  SET_SELECTED_BRANCH: 'SET_SELECTED_BRANCH',
  UPDATE_ORDER_STATUS: 'UPDATE_ORDER_STATUS',
  FILTER_ORDERS: 'FILTER_ORDERS',
  REFRESH_ORDERS: 'REFRESH_ORDERS'
};

const safeTimestamp = (input) => {
  if (!input) return 0;
  try {
    if (typeof input === 'number') return input;
    const t = new Date(input).getTime();
    return Number.isFinite(t) ? t : 0;
  } catch (e) {
    return 0;
  }
};

const orderReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };

    case ActionTypes.SET_ORDERS:
      const versions = { ...state.orderVersions };
      (action.payload || []).forEach((o) => {
        const ts = safeTimestamp(o?.updatedAt) || Date.now();
        versions[o._id] = Math.max(versions[o._id] || 0, ts);
      });
      return { ...state, orders: action.payload, orderVersions: versions, loading: false, error: null };

    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload, loading: false };

    case ActionTypes.SET_SELECTED_BRANCH:
      return { ...state, selectedBranch: action.payload };

    case ActionTypes.UPDATE_ORDER_STATUS: {
      const { orderId, status, version } = action.payload;
      const currentVersion = state.orderVersions[orderId] || 0;
      const nextVersion = version || Date.now();
      if (nextVersion < currentVersion) {
        return state;
      }
      return {
        ...state,
        orderVersions: { ...state.orderVersions, [orderId]: nextVersion },
        orders: state.orders.map(order =>
          order._id === orderId
            ? { ...order, status }
            : order
        )
      };
    }

    case ActionTypes.FILTER_ORDERS:
      return { ...state, filters: { ...state.filters, ...action.payload } };

    case ActionTypes.REFRESH_ORDERS:
      return { ...state, loading: true };

    default:
      return state;
  }
};

export const OrderProvider = ({ children }) => {
  const [state, dispatch] = useReducer(orderReducer, initialState);
  const { isAuthenticated } = useAuth();
  const { selectedBranch, listenAllMode, allBranches } = useBranch();

  const fetchOrders = async (branchId = null) => {
    if (!isAuthenticated) {
      return;
    }

    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      if (listenAllMode && allBranches && allBranches.length > 0) {
        

        const branchOrderPromises = allBranches.map(branch =>
          getBranchOrders(branch._id).catch(err => {
            
            return { orders: [] };
          })
        );

        const allResponses = await Promise.all(branchOrderPromises);

        const aggregatedOrders = allResponses.reduce((acc, response) => {
          return [...acc, ...(response.orders || [])];
        }, []);

        aggregatedOrders.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateA - dateB;
        });

        
        dispatch({ type: ActionTypes.SET_ORDERS, payload: aggregatedOrders });
        return;
      }

      const targetBranchId = branchId || selectedBranch?._id;

      let response;
      if (targetBranchId) {
        
        response = await getBranchOrders(targetBranchId);
      } else {
        
        response = await getAllOrders();
      }

      const sortedOrders = (response.orders || []).sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB;
      });
      dispatch({ type: ActionTypes.SET_ORDERS, payload: sortedOrders });
    } catch (error) {
      
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
    }
  };

  const updateOrderStatus = (orderId, status) => {
    dispatch({
      type: ActionTypes.UPDATE_ORDER_STATUS,
      payload: { orderId, status, version: Date.now() }
    });
  };

  const applyStatusUpdate = useCallback((orderId, status, version) => {
    dispatch({
      type: ActionTypes.UPDATE_ORDER_STATUS,
      payload: { orderId, status, version }
    });
  }, []);

  const getOrdersByStatus = (status) => {
    if (!status) return state.orders;

    if (status === ORDER_STATUS_CATEGORIES.READY_TO_PICKUP) {
      const readyStatuses = ['Ready to Pickup', 'Out For Delivery', 'Rider Assigned', 'Rider Reached Pickup', 'Rider Picked Up'];
      return state.orders.filter(order =>
        readyStatuses.some(readyStatus =>
          order.status?.trim().toLowerCase() === readyStatus.trim().toLowerCase()
        )
      );
    }

    return state.orders.filter(order =>
      order.status?.trim().toLowerCase() === status.trim().toLowerCase()
    );
  };

  const getOrderCounts = () => {
    return {
      confirmed: getOrdersByStatus(ORDER_STATUS_CATEGORIES.CONFIRMED).length,
      preparing: getOrdersByStatus(ORDER_STATUS_CATEGORIES.PREPARING).length,
      readyToPickup: getOrdersByStatus(ORDER_STATUS_CATEGORIES.READY_TO_PICKUP).length
    };
  };

  const setSelectedBranch = (branch) => {
    dispatch({ type: ActionTypes.SET_SELECTED_BRANCH, payload: branch });
  };

  const refreshOrders = useCallback(() => {
    if (!isAuthenticated) return;
    dispatch({ type: ActionTypes.REFRESH_ORDERS });
    fetchOrders(listenAllMode ? null : selectedBranch?._id);
  }, [isAuthenticated, listenAllMode, selectedBranch?._id]);

  useEffect(() => {
    if (!isAuthenticated) return;

    if (!listenAllMode && !selectedBranch?._id) {
      
      return;
    }

    fetchOrders(selectedBranch?._id);

    // Poll every 10 seconds as fallback for missed socket events
    const pollInterval = setInterval(() => {
      
      fetchOrders(selectedBranch?._id);
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [isAuthenticated, listenAllMode, selectedBranch?._id]);

  const value = {
    ...state,
    fetchOrders,
    updateOrderStatus,
    applyStatusUpdate,
    getOrdersByStatus,
    getOrderCounts,
    setSelectedBranch,
    refreshOrders
  };

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
};

export const useOrders = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
};

export default OrderContext;